/**
 * Onboarding Dialog
 *
 * A dialog wrapper with support for non-dismissible mode.
 * Used for onboarding flows that require completion.
 */

import * as React from 'react';
import { cn } from '@newcore/ui/cn';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { OnboardingDialogProps, OnboardingDialogSize } from './types';

const SIZE_CLASSES: Record<OnboardingDialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

/**
 * Onboarding Dialog Component
 *
 * @example
 * <Onboarding.Dialog open={open} dismissible={false} size="md">
 *   <Onboarding.StepIndicator current={1} total={4} />
 *   <Onboarding.Header>...</Onboarding.Header>
 *   <Onboarding.Body>...</Onboarding.Body>
 *   <Onboarding.Footer>...</Onboarding.Footer>
 * </Onboarding.Dialog>
 */
export const OnboardingDialog: React.FC<OnboardingDialogProps> = ({
  open,
  onOpenChange,
  dismissible = true,
  size = 'md',
  children,
  className,
}) => {
  // Prevent closing when not dismissible
  const handleOpenChange = dismissible ? onOpenChange : undefined;

  const preventClose = (e: Event) => {
    if (!dismissible) {
      e.preventDefault();
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        {/* Overlay with blur effect */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />

        {/* Dialog Content */}
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]',
            'w-full',
            SIZE_CLASSES[size],
            'bg-primary border border-secondary rounded-2xl shadow-2xl',
            'p-8',
            'duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            className
          )}
          onEscapeKeyDown={preventClose}
          onPointerDownOutside={preventClose}
          onInteractOutside={preventClose}
        >
          {/* Accessibility - hidden title and description */}
          <DialogPrimitive.Title className='sr-only'>
            Onboarding
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className='sr-only'>
            Complete the onboarding steps to set up your account
          </DialogPrimitive.Description>

          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
